// Admin portal-settings slice. Loads the full merged settings (including the
// admin-only keys the public config omits) plus the built-in defaults, and saves
// a partial update. The editor page holds the in-progress form; this slice owns
// the server-synced "saved" copy that the form resets/diffs against.
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { adminFetch, AdminAuthError } from '../api/admin-client.js';
import { clearAdminSession } from './admin-auth-slice.js';

const handleThunkError = (err, dispatch, rejectWithValue) => {
  if (err instanceof AdminAuthError) dispatch(clearAdminSession());
  return rejectWithValue(err.message);
};

export const fetchAdminSettings = createAsyncThunk(
  'adminSettings/fetch',
  async (_arg, { dispatch, rejectWithValue }) => {
    try {
      return await adminFetch('/api/admin/settings');
    } catch (err) {
      return handleThunkError(err, dispatch, rejectWithValue);
    }
  },
);

// `values` is the full editable settings object; the server validates and
// upserts each key, then returns the fresh merged settings.
export const saveAdminSettings = createAsyncThunk(
  'adminSettings/save',
  async (values, { dispatch, rejectWithValue }) => {
    try {
      const { settings } = await adminFetch('/api/admin/settings', {
        method: 'PUT',
        body: { settings: values },
      });
      return settings;
    } catch (err) {
      return handleThunkError(err, dispatch, rejectWithValue);
    }
  },
);

const initialState = {
  settings: null,
  defaults: null,
  loading: false,
  error: null,
  saving: false,
  saveError: null,
};

const adminSettingsSlice = createSlice({
  name: 'adminSettings',
  initialState,
  reducers: {
    clearSaveError: (s) => { s.saveError = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminSettings.pending, (s) => {
        s.loading = true;
        s.error = null;
      })
      .addCase(fetchAdminSettings.fulfilled, (s, a) => {
        s.loading = false;
        s.settings = a.payload.settings;
        s.defaults = a.payload.defaults;
      })
      .addCase(fetchAdminSettings.rejected, (s, a) => {
        s.loading = false;
        s.error = a.payload || 'Could not load portal settings.';
      })
      .addCase(saveAdminSettings.pending, (s) => {
        s.saving = true;
        s.saveError = null;
      })
      .addCase(saveAdminSettings.fulfilled, (s, a) => {
        s.saving = false;
        s.settings = a.payload;
      })
      .addCase(saveAdminSettings.rejected, (s, a) => {
        s.saving = false;
        s.saveError = a.payload || 'Could not save settings. Please try again.';
      });
  },
});

export const { clearSaveError } = adminSettingsSlice.actions;
export default adminSettingsSlice.reducer;
